import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const MODES = {
  test: { label: "TEST DATA ONLY", phrase: "RESET TEST DATA", action: "DELETE TEST DATA",
    text: "Removes test members (emails with +test, test_ or wh_), their invitations, reviews and orders, plus leftover orphan records. Your admin account, unused and revoked codes are kept. Test customers lose the member tag in Shopify." },
  all: { label: "ALL APPLICATION DATA", phrase: "DELETE ALL APPLICATION DATA", action: "DELETE ALL APPLICATION DATA",
    text: "IRREVERSIBLE. Deletes every member, user (except super admin), invitation, review, order record, referral and audit log in this app. Shopify customers, orders and products are not deleted; only the member tag is removed. A backup download is required first." },
};
const OUTLINE = "tracking-[0.2em] text-xs border border-[#C9BCA2] bg-transparent text-[#2E2A22] hover:bg-[#E8DFCF]";
const DARK = "tracking-[0.2em] text-xs bg-[#2E2A22] text-[#F3EEE4] hover:bg-[#201C15]";
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const CountTable = ({ rows, testid }) => (
  <table className="w-full text-xs" data-testid={testid}>
    <thead><tr className="text-[#9C907A] tracking-[0.15em]"><th className="text-left py-1">COLLECTION</th>{rows[0].slice(1).map((h) => <th key={h} className="text-right">{h}</th>)}</tr></thead>
    <tbody>{rows.slice(1).map((r) => (
      <tr key={r[0]} className="border-t border-[#E8DFCF]">{r.map((v, i) => <td key={i} className={i ? "text-right py-1" : "py-1"}>{v}</td>)}</tr>
    ))}</tbody>
  </table>
);

const JobResult = ({ job }) => (
  <div className="space-y-2" data-testid="reset-result">
    <p className={`text-xs tracking-[0.2em] ${job.status === "COMPLETED" ? "text-[#6B6151]" : "text-[#9B3B2E]"}`} data-testid="reset-result-status">{job.status}</p>
    {job.error && <p className="text-xs text-[#9B3B2E]" data-testid="reset-result-error">{job.error}</p>}
    {job.remaining && <CountTable testid="reset-result-table" rows={[["", "DELETED", "REMAINING"], ...Object.keys(job.remaining).map((c) => [c, job.deleted?.[c] ?? 0, job.remaining[c]])]} />}
    {job.shopify && <p className="text-xs text-[#6B6151]" data-testid="reset-result-shopify">Shopify untagged: {job.shopify.untagged.length} · already gone from Shopify: {job.shopify.already_missing.length}{job.shopify.failed ? ` · failed: customer ${job.shopify.failed.customer}` : ""}</p>}
  </div>
);

export const ResetTestData = ({ onDone }) => {
  const [mode, setMode] = useState("test");
  const [preview, setPreview] = useState(null);
  const [job, setJob] = useState(null);
  const [phrase, setPhrase] = useState("");
  const [backupId, setBackupId] = useState(null);
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const m = MODES[mode];

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const switchMode = (k) => { setMode(k); setPreview(null); setJob(null); setPhrase(""); setBackupId(null); };
  const loadPreview = () => run(async () => { setJob(null); setPreview((await api.get(`/admin/reset-preview?scope=${mode}`)).data); });
  const backup = () => run(async () => {
    const r = await api.get("/admin/reset-backup", { responseType: "blob" });
    const id = JSON.parse(await r.data.text()).backup_id;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data); a.download = `serenismm-backup-${id}.json`; a.click();
    setBackupId(id); toast.success("Backup downloaded");
  });
  const execute = () => run(async () => {
    setAsk(false);
    const r = await api.post("/admin/reset-test-data", { scope: mode, confirm: phrase, untag_shopify: true, backup_id: backupId, acknowledge_irreversible: mode === "all" });
    let j = { status: "RUNNING" };
    while (j.status === "RUNNING") { await sleep(1500); j = (await api.get(`/admin/reset-jobs/${r.data.job_id}`)).data; }
    setJob(j); setPreview(null); setPhrase(""); setBackupId(null);
    j.status === "COMPLETED" ? toast.success("Reset completed") : toast.error("Reset did not complete — see details");
    onDone?.();
  });

  const cols = preview ? Object.keys(preview.totals) : [];
  const ready = phrase === m.phrase && (mode === "test" || backupId);
  return (
    <div className="bg-[#FBF8F1] border border-[#DCD1BC] p-6 max-w-2xl space-y-4" data-testid="reset-test-data">
      <p className="text-[11px] tracking-[0.25em] text-[#A98F63]">RESET TEST DATA</p>
      <div className="flex gap-4">{Object.entries(MODES).map(([k, v]) => (
        <button key={k} data-testid={`reset-mode-${k}`} onClick={() => switchMode(k)} className={`text-[11px] tracking-[0.2em] pb-1 ${mode === k ? "border-b-2 border-[#A98F63] text-[#2E2A22]" : "text-[#9C907A]"}`}>{v.label}</button>
      ))}</div>
      <p className={`text-sm ${mode === "all" ? "text-[#9B3B2E]" : "text-[#6B6151]"}`} data-testid="reset-mode-text">{m.text}</p>
      <Button data-testid="reset-preview-btn" disabled={busy} onClick={loadPreview} className={OUTLINE}>PREVIEW</Button>
      {preview && (
        <div className="space-y-3" data-testid="reset-preview">
          <CountTable testid="reset-preview-table" rows={[["", "TOTAL", "DELETE", "KEEP"], ...cols.map((c) => [c, preview.totals[c], preview.to_delete[c], preview.remaining_after[c]])]} />
          <p className="text-xs text-[#6B6151]" data-testid="reset-preview-emails">{preview.test_member_emails ? `Test members: ${preview.test_member_emails.join(", ") || "none"} · ` : ""}Shopify customers to untag: {preview.shopify_customers_to_untag}</p>
          {preview.preserved && <ul className="text-xs text-[#6B6151] list-disc pl-5" data-testid="reset-preview-preserved">{preview.preserved.map((p) => <li key={p}>Kept: {p}</li>)}</ul>}
          {mode === "all" && <Button data-testid="reset-backup-btn" disabled={busy} onClick={backup} className={OUTLINE}>{backupId ? "BACKUP DOWNLOADED" : "DOWNLOAD BACKUP"}</Button>}
          <Input data-testid="reset-confirm-input" placeholder={`Type ${m.phrase}`} value={phrase} onChange={(e) => setPhrase(e.target.value)} className="bg-transparent border-[#C9BCA2]" />
          <Button data-testid="reset-confirm-btn" disabled={busy || !ready} onClick={() => (mode === "all" ? setAsk(true) : execute())} className={DARK}>{busy ? "WORKING…" : m.action}</Button>
        </div>
      )}
      {job && <JobResult job={job} />}
      <AlertDialog open={ask} onOpenChange={setAsk}>
        <AlertDialogContent data-testid="reset-final-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete all application data?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone. Everything listed in the preview is deleted except your admin account. Shopify customers, orders and products stay.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-final-cancel">CANCEL</AlertDialogCancel>
            <AlertDialogAction data-testid="reset-final-confirm" onClick={execute} className={DARK}>YES, DELETE EVERYTHING</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
